import React, { useEffect, useState, useRef } from "react";
import {
  Container, Typography, Box, Paper, Slider,
  FormControlLabel, Switch, Button, useMediaQuery,
  FormControl, InputLabel, Select, MenuItem,
} from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import AuthHeader from "../components/AuthHeader";

// 中2(J2)「書き起こし」専用ページ。
// J1 の SentenceViewerPage を簡略化したもの:
//  - 表示元セレクタ無し（書き起こし transcribed のみ）
//  - 日本語訳・認識済み(recognized)表示無し
//  - 再生ログ無効（Option A）
//  - 週選択 / 英語表示トグル / 再生速度 / 連続再生 / ループ再生 は維持
function SentenceViewerJ2Page({ API_BASE, AUDIO_BASE }) {
  const isSmallScreen = useMediaQuery("(max-width:600px)");
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const searchParams = new URLSearchParams(location.search);
  const weekNumParam = searchParams.get("week_num");
  const hasWeekFilter = Boolean(weekNumParam);

  const [sentences, setSentences] = useState([]);
  const [weekOptions, setWeekOptions] = useState([]);
  const [selectedWeek, setSelectedWeek] = useState(weekNumParam || "");
  const [source, setSource] = useState("recognized");
  const [playbackRate, setPlaybackRate] = useState(1.0);
  const [loopAll, setLoopAll] = useState(false);
  const [autoNext, setAutoNext] = useState(false);
  const [showEnglish, setShowEnglish] = useState(true);
  const [individualEnglishShow, setIndividualEnglishShow] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(null);
  const [playingKey, setPlayingKey] = useState(null);
  const audioRefs = useRef({});
  const sentenceRefs = useRef({});
  const isPlayingRef = useRef(false);
  const [autoNextTimer, setAutoNextTimer] = useState(null);

  // ログイン状態チェック
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login', { state: { from: '/sentence_viewer_j2' }, replace: true });
    }
  }, [isAuthenticated, navigate]);

  // 週リスト取得
  useEffect(() => {
    if (hasWeekFilter) {
      setSelectedWeek(weekNumParam);
    } else {
      fetch(`${API_BASE}/j2/transcription_weeks`)
        .then((res) => res.json())
        .then((data) => {
          const weeks = Array.isArray(data) ? data.map((w) => w.week_num) : [];
          setWeekOptions(weeks);
          if (weeks.length > 0) {
            setSelectedWeek(weeks[0]);
          }
        })
        .catch((err) => console.error("week fetch error:", err));
    }
  }, []);

  // センテンス取得（表示元: recognized=認識済み / transcribed=書き起こし）
  useEffect(() => {
    if (selectedWeek) {
      const endpoint = source === "recognized"
        ? `j2/recognized_sentences_by_week/${selectedWeek}`
        : `j2/transcribed_sentences_by_week/${selectedWeek}`;
      fetch(`${API_BASE}/${endpoint}`)
        .then((res) => res.json())
        .then((data) => {
          const sentenceData = Array.isArray(data) ? data : [];
          setSentences(sentenceData);
          setIndividualEnglishShow(new Array(sentenceData.length).fill(true));
        })
        .catch((err) => console.error("API fetch error:", err));
    }
  }, [selectedWeek, source]);

  useEffect(() => {
    Object.values(audioRefs.current).forEach((audio) => {
      if (audio) audio.playbackRate = playbackRate;
    });
  }, [playbackRate, sentences]);

  useEffect(() => {
    if (currentIndex !== null && sentenceRefs.current[currentIndex]) {
      sentenceRefs.current[currentIndex].scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  }, [currentIndex]);

  const handleGlobalEnglishToggle = (checked) => {
    setShowEnglish(checked);
    setIndividualEnglishShow(new Array(sentences.length).fill(checked));
  };

  const handleIndividualEnglishToggle = (index, checked) => {
    const newIndividualShow = [...individualEnglishShow];
    newIndividualShow[index] = checked;
    setIndividualEnglishShow(newIndividualShow);

    const allTrue = newIndividualShow.every((show) => show);
    const allFalse = newIndividualShow.every((show) => !show);
    if (allTrue) {
      setShowEnglish(true);
    } else if (allFalse) {
      setShowEnglish(false);
    }
  };

  const stopAllAudio = () => {
    Object.values(audioRefs.current).forEach((audio) => {
      if (audio && !audio.paused) {
        audio.pause();
        audio.currentTime = 0;
      }
    });
    if (autoNextTimer) {
      clearTimeout(autoNextTimer);
      setAutoNextTimer(null);
    }
    isPlayingRef.current = false;
    setCurrentIndex(null);
    setPlayingKey(null);
  };

  const handlePlay = (index, audioIndex = 0) => {
    stopAllAudio();

    const key = `${index}_${audioIndex}`;
    const audio = audioRefs.current[key];
    const currentSentence = sentences[index];

    if (audio && currentSentence) {
      audio.play();
      isPlayingRef.current = true;
      setCurrentIndex(index);
      setPlayingKey(key);
    }
  };

  // ネイティブ音声（アメリカ人男性/女性）の再生。単発再生で連続/ループ対象外。
  const handlePlayVoice = (index, voice) => {
    stopAllAudio();

    const key = `${index}_voice_${voice}`;
    const audio = audioRefs.current[key];

    if (audio) {
      audio.play();
      isPlayingRef.current = true;
      setCurrentIndex(index);
      setPlayingKey(key);
    }
  };

  const handleVoiceEnded = () => {
    isPlayingRef.current = false;
    setCurrentIndex(null);
    setPlayingKey(null);
  };

  const handleEnded = () => {
    if (!isPlayingRef.current || !playingKey) return;

    const [indexStr, audioIndexStr] = playingKey.split("_");
    const index = parseInt(indexStr);
    const audioIndex = parseInt(audioIndexStr);
    const currentItem = sentences[index];
    const nextAudioIndex = audioIndex + 1;

    if (Array.isArray(currentItem.audio_files) && nextAudioIndex < currentItem.audio_files.length) {
      if (autoNext) {
        const timer = setTimeout(() => handlePlay(index, nextAudioIndex), 1000);
        setAutoNextTimer(timer);
      } else {
        isPlayingRef.current = false;
        setCurrentIndex(null);
        setPlayingKey(null);
      }
    } else {
      const nextIndex = index + 1;
      if (autoNext && nextIndex < sentences.length) {
        const timer = setTimeout(() => handlePlay(nextIndex, 0), 1000);
        setAutoNextTimer(timer);
      } else if (loopAll) {
        const timer = setTimeout(() => handlePlay(index, 0), 1000);
        setAutoNextTimer(timer);
      } else {
        isPlayingRef.current = false;
        setCurrentIndex(null);
        setPlayingKey(null);
      }
    }
  };

  if (!isAuthenticated) {
    return null;
  }

  return (
    <>
      <AuthHeader />
      <Container maxWidth="md" sx={{ paddingBottom: 24 }}>
        <Typography variant="h5" sx={{ mt: 2, mb: 2 }}>
          中2 重要例文
        </Typography>

        {!hasWeekFilter && (
          <>
            <Box sx={{ marginBottom: 2 }}>
              <FormControl fullWidth>
                <InputLabel id="source-label">表示元</InputLabel>
                <Select
                  labelId="source-label"
                  value={source}
                  label="表示元"
                  onChange={(e) => setSource(e.target.value)}
                >
                  <MenuItem value="recognized">認識済み（recognized_sentences）</MenuItem>
                  <MenuItem value="transcribed">書き起こし（transcribed_sentences）</MenuItem>
                </Select>
              </FormControl>
            </Box>

            <Box sx={{ marginBottom: 4 }}>
              <FormControl fullWidth>
                <InputLabel id="week-label">週番号を選択</InputLabel>
                <Select
                  labelId="week-label"
                  value={selectedWeek}
                  label="週番号を選択"
                  onChange={(e) => setSelectedWeek(e.target.value)}
                >
                  {weekOptions.map((week, i) => (
                    <MenuItem key={i} value={week}>
                      {week}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </>
        )}

        <Box sx={{ marginBottom: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
          <FormControlLabel
            control={
              <Switch
                checked={showEnglish}
                onChange={(e) => handleGlobalEnglishToggle(e.target.checked)}
              />
            }
            label="全て英語表示"
          />
        </Box>

        <Box display="flex" flexDirection="column" gap={3}>
          {Array.isArray(sentences) &&
            sentences.map((item, index) => (
              <Paper
                key={item.id || index}
                ref={(el) => (sentenceRefs.current[index] = el)}
                sx={{
                  padding: 2,
                  backgroundColor: currentIndex === index ? "#e3f2fd" : "inherit",
                }}
              >
                {source === "recognized" && item.sentence_jp && (
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    {item.sentence_jp}
                  </Typography>
                )}

                <Box sx={{ marginBottom: 2 }}>
                  <Typography
                    variant="h6"
                    gutterBottom
                    sx={{
                      visibility: individualEnglishShow[index] ? 'visible' : 'hidden',
                      minHeight: '1.5rem',
                      lineHeight: 1.5
                    }}
                  >
                    {item.sentence || ' '}
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <FormControlLabel
                    control={
                      <Switch
                        size="small"
                        checked={individualEnglishShow[index] || false}
                        onChange={(e) => handleIndividualEnglishToggle(index, e.target.checked)}
                      />
                    }
                    label="英語"
                    sx={{ marginRight: 2 }}
                  />

                  {Array.isArray(item.audio_files) && item.audio_files.map((file, i) => (
                    <React.Fragment key={`${index}_${i}`}>
                      {playingKey === `${index}_${i}` ? (
                        <Button variant="outlined" color="error" onClick={stopAllAudio} sx={{ mr: 1 }}>
                          停止
                        </Button>
                      ) : (
                        <Button
                          variant="contained"
                          onClick={() => handlePlay(index, i)}
                          sx={{ mr: 1 }}
                        >
                          再生
                        </Button>
                      )}
                      <audio
                        style={{ display: "none" }}
                        ref={(el) => (audioRefs.current[`${index}_${i}`] = el)}
                        onEnded={handleEnded}
                      >
                        <source src={`${AUDIO_BASE}/audio_clipped/${file}`} type="audio/mpeg" />
                      </audio>
                    </React.Fragment>
                  ))}

                  {/* ネイティブ例文音声（書き起こし表示のみ・存在する声のみボタン表示） */}
                  {source === "transcribed" && Array.isArray(item.audio_files) && item.audio_files[0] && (
                    <>
                      {item.voices?.us_male && (
                        <React.Fragment>
                          {playingKey === `${index}_voice_us_male` ? (
                            <Button variant="outlined" color="error" onClick={stopAllAudio} sx={{ mr: 1, minWidth: 140 }}>
                              停止
                            </Button>
                          ) : (
                            <Button
                              variant="contained"
                              color="secondary"
                              onClick={() => handlePlayVoice(index, "us_male")}
                              sx={{ mr: 1, minWidth: 140 }}
                            >
                              アメリカ人男性
                            </Button>
                          )}
                          <audio
                            style={{ display: "none" }}
                            ref={(el) => (audioRefs.current[`${index}_voice_us_male`] = el)}
                            onEnded={handleVoiceEnded}
                          >
                            <source src={`${AUDIO_BASE}/audio_clipped/${item.audio_files[0]}?voice=us_male`} type="audio/mpeg" />
                          </audio>
                        </React.Fragment>
                      )}

                      {item.voices?.us_female && (
                        <React.Fragment>
                          {playingKey === `${index}_voice_us_female` ? (
                            <Button variant="outlined" color="error" onClick={stopAllAudio} sx={{ mr: 1, minWidth: 140 }}>
                              停止
                            </Button>
                          ) : (
                            <Button
                              variant="contained"
                              color="secondary"
                              onClick={() => handlePlayVoice(index, "us_female")}
                              sx={{ mr: 1, minWidth: 140 }}
                            >
                              アメリカ人女性
                            </Button>
                          )}
                          <audio
                            style={{ display: "none" }}
                            ref={(el) => (audioRefs.current[`${index}_voice_us_female`] = el)}
                            onEnded={handleVoiceEnded}
                          >
                            <source src={`${AUDIO_BASE}/audio_clipped/${item.audio_files[0]}?voice=us_female`} type="audio/mpeg" />
                          </audio>
                        </React.Fragment>
                      )}
                    </>
                  )}
                </Box>
              </Paper>
            ))}
        </Box>

        <Box
          sx={{
            position: "fixed",
            bottom: 0,
            left: 0,
            width: "100%",
            backgroundColor: "#fff",
            borderTop: "1px solid #ccc",
            padding: 2,
            display: "flex",
            flexDirection: isSmallScreen ? "column" : "row",
            gap: 2,
            justifyContent: "center",
            alignItems: "center",
            zIndex: 1000,
          }}
        >
          <Box minWidth={200}>
            <Typography gutterBottom>再生速度：{playbackRate.toFixed(1)}x</Typography>
            <Slider
              value={playbackRate}
              onChange={(_, val) => setPlaybackRate(val)}
              min={0.5}
              max={1.5}
              step={0.1}
              valueLabelDisplay="auto"
              marks={[{ value: 0.5, label: "0.5x" }, { value: 1.0, label: "1.0x" }, { value: 1.5, label: "1.5x" }]}
            />
          </Box>
          <Box display="flex" flexDirection="row" gap={4}>
            <FormControlLabel
              control={<Switch checked={loopAll} onChange={(e) => setLoopAll(e.target.checked)} />}
              label="ループ再生"
            />
            <FormControlLabel
              control={<Switch checked={autoNext} onChange={(e) => setAutoNext(e.target.checked)} />}
              label="連続再生"
            />
          </Box>
        </Box>
      </Container>
    </>
  );
}

export default SentenceViewerJ2Page;
